/* eslint-disable react/jsx-no-duplicate-props */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { InputHelperText, useInput } from 'react-admin';

import { formatMoneyInput, fromMoney, parseMoneyInput } from '@src/utils/moneyHelpers';

const MoneyInput = ({
    source,
    label,
    helperText,
    InputProps,
    inputProps,
    fullWidth,
    disabled,
    validate,
    variant = 'filled',
    margin = 'dense',
    ...rest
}) => {
    const {
        field,
        fieldState: { error, invalid, isTouched },
        isRequired,
        formState: { isSubmitted },
    } = useInput({
        source,
        validate,
        ...rest,
    });

    const [editingValue, setEditingValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const displayValue = isFocused ? editingValue : formatMoneyInput(field.value);

    const handleFocus = useCallback((event) => {
        setIsFocused(true);
        setEditingValue(field.value ? String(fromMoney(field.value)) : '');
        field.onFocus(event);
    }, [field]);

    const handleChange = useCallback((event) => {
        const normalized = event.target.value.replace(/,/g, '');

        if (normalized === '' || /^\d*\.?\d{0,2}$/.test(normalized)) {
            setEditingValue(normalized);
        }
    }, []);

    const handleBlur = useCallback((event) => {
        setIsFocused(false);
        const money = parseMoneyInput(editingValue);
        field.onChange(money);
        field.onBlur(event);
    }, [editingValue, field]);

    const showError = Boolean((isTouched || isSubmitted) && invalid);

    return (
        <TextField
            {...field}
            label={label}
            required={isRequired}
            variant={variant}
            margin={margin}
            fullWidth={fullWidth}
            disabled={disabled}
            value={displayValue}
            error={showError}
            helperText={
                <InputHelperText
                    error={error?.message}
                    helperText={helperText}
                    touched={isTouched || isSubmitted}
                />
            }
            InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                ...InputProps,
            }}
            inputProps={{
                inputMode: 'decimal',
                ...inputProps,
            }}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
        />
    );
};

MoneyInput.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
    helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
    InputProps: PropTypes.object,
    inputProps: PropTypes.object,
    fullWidth: PropTypes.bool,
    disabled: PropTypes.bool,
    validate: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.arrayOf(PropTypes.func),
    ]),
    variant: PropTypes.string,
    margin: PropTypes.string,
};

export default MoneyInput;
